import moment from 'moment';
import { useQuery } from '@tanstack/react-query';

import { API } from 'src/reactQuery/Api';
import { QUERY_KEYS } from 'src/reactQuery/QueryKeys';
import { RequestDataArray } from 'src/types/requestData';
import { daysUntilToday, forceString } from 'src/utils/datetime';
import {
  getRequest,
  paginationMapper,
  resolveClearFilters,
  resolveQueryString,
} from 'src/utils/request';

export type VerificationType =
  | 'matriculation_files'
  | 'work_permissions'
  | 'certificate_files'
  | 'pictures'
  | 'user_languages'
  | 'cancellation_proofs'
  | 'without_proof';

const mapVerificationFiles = (
  attributes: UCM.VerificationFilesRawType,
  id: string,
): UCM.VerificationFilesType => ({
  id,
  uuid: attributes.uuid,
  createdAt: daysUntilToday(attributes.created_at, false, true) ?? '-',
  issueDate: attributes.issue_date,
  expirationDate: attributes.expiration_date,
  statusName: attributes.status_name,
  user: {
    id: attributes.user.id,
    uuid: attributes.user.uuid,
    firstName: attributes.user.first_name,
    lastName: attributes.user.last_name,
  },
  url: attributes.document_url,
  contentType: attributes.document_content_type,
  fileName: attributes.document_name,
  certificateCode: attributes?.certificate_code ?? undefined,
});

const mapVerificationCancellationProofs = (
  attributes: UCM.VerificationCancellationProofsRawType,
  id: string,
): UCM.VerificationCancellationProofsType => {
  return {
    id,
    uuid: attributes.uuid,
    userId: attributes.user_id,
    cancellationNoticeId: attributes.cancellation_notice_id,
    rejectionReason: attributes.rejection_reason,
    adminComment: attributes.admin_comment,
    createdAt: attributes.proof
      ? daysUntilToday(attributes.proof.created_at, false, true)
      : null,
    user: {
      id: attributes.user.id,
      uuid: attributes.user.uuid,
      firstName: attributes.user.first_name,
      lastName: attributes.user.last_name,
    },
    cancellationNotice: {
      id: attributes.cancellation_notice.id,
      createdAt:
        daysUntilToday(
          attributes.cancellation_notice.created_at,
          false,
          true,
        ) ?? '-',
      verificationOverdue: attributes.cancellation_notice.verification_overdue,
      explanation: attributes.cancellation_notice?.explanation,
      reason: attributes.cancellation_notice?.reason,
      shiftDates: attributes.cancellation_notice?.shift_dates,
    },
    job: {
      id: attributes.job.id,
      projectUuid: attributes.job.project_uuid,
    },
    proof: attributes.proof
      ? {
          createdAt: attributes.proof.created_at,
          verificationOverdue: attributes.proof.verification_overdue,
          fileName: attributes.proof.file_name,
        }
      : null,
    firstDayOfSickness: attributes.first_day_of_sickness
      ? forceString(attributes.first_day_of_sickness, moment.HTML5_FMT.DATE)
      : null,
    healthInsurance: attributes.health_insurance,
  };
};

const mapVerificationLanugages = (
  attributes: UCM.VerificationLanugagesRawType,
  id: string,
): UCM.VerificationLanugagesType => ({
  id,
  uuid: attributes.uuid,
  createdAt: daysUntilToday(attributes.created_at, false, true) ?? '-',
  user: {
    id: attributes.user.id,
    uuid: attributes.user.uuid,
    fullName: attributes.user.full_name,
    firstName: attributes.user.first_name,
    lastName: attributes.user.last_name,
    birthDate: attributes.user.birth_date,
    birthCountry: attributes.user.birth_country,
    gender: attributes.user.gender,
  },
  language: {
    code: attributes.language.code,
    name: attributes.language.name,
  },
  status: attributes.status,
  rejectionReason: attributes.rejection_reason,
  ratings: attributes.ratings,
  fileName: attributes.video.file_name,
  contentType: attributes.video.content_type,
  url: attributes.video.url,
  uploadedAt: attributes.video.uploaded_at,
});

const mapVerificationPicutesFiles = (
  attributes: UCM.VerificationPicutesFilesRawType,
  id: string,
): UCM.VerificationPicutesFilesType => ({
  id,
  uuid: attributes.uuid,
  createdAt: daysUntilToday(attributes.created_at, false, true) ?? '-',
  thumbAssetUrl: attributes.thumb_asset_url,
  url: attributes.medium_asset_url,
  contentType: attributes.asset_content_type,
  mediaTypeCode: attributes.media_type_code,
  fileName: attributes.asset_file_name,
  issueDate: attributes.issue_date,
  expirationDate: attributes.expiration_date,
  statusName: attributes.status_name,
  user: {
    id: attributes.user.id,
    uuid: attributes.user.uuid,
    firstName: attributes.user.first_name,
    lastName: attributes.user.last_name,
  },
});

const getVerificationFiles = async (
  type: VerificationType,
  filters: Record<string, string | boolean | number | string[]>,
  params: Record<string, string | number>,
) => {
  const query = resolveQueryString([filters], params);

  let url: string = type;

  if (
    type.match(
      /matriculation_files|work_permissions|certificate_files|pictures/,
    )
  ) {
    url = `assets/${type}`;
  }

  if (type === 'without_proof') {
    url = `cancellation_notices/${type}`;
  }

  const {
    data: { data, meta },
  } = await getRequest<
    RequestDataArray<
      | UCM.VerificationFilesRawType
      | UCM.VerificationCancellationProofsRawType
      | UCM.VerificationLanugagesRawType
      | UCM.VerificationPicutesFilesRawType,
      UCM.MetaRawType
    >
  >(API.GET.getVerificationFiles(url, query));

  let mappedData: Array<
    | UCM.VerificationFilesType
    | UCM.VerificationCancellationProofsType
    | UCM.VerificationLanugagesType
    | UCM.VerificationPicutesFilesType
  > = data.map(({ attributes, id }) =>
    mapVerificationFiles(attributes as UCM.VerificationFilesRawType, id),
  );

  if (type.match(/cancellation_proofs|without_proof/)) {
    mappedData = data.map(({ attributes, id }) =>
      mapVerificationCancellationProofs(
        attributes as UCM.VerificationCancellationProofsRawType,
        id,
      ),
    );
  } else if (type === 'user_languages') {
    mappedData = data.map(({ attributes, id }) =>
      mapVerificationLanugages(
        attributes as UCM.VerificationLanugagesRawType,
        id,
      ),
    );
  } else if (type === 'pictures') {
    mappedData = data.map(({ attributes, id }) =>
      mapVerificationPicutesFiles(
        attributes as UCM.VerificationPicutesFilesRawType,
        id,
      ),
    );
  }

  return {
    meta: paginationMapper(meta),
    data: mappedData,
  };
};

export const useGetVerificationFiles = ({
  type,
  filters = {},
  params,
  enabled = true,
}: {
  type: VerificationType;
  filters?: Record<string, string | number | string[] | boolean>;
  params: Record<string, string | number>;
  enabled?: boolean;
}) => {
  const clearFilters = resolveClearFilters(filters);
  return useQuery({
    queryKey: [QUERY_KEYS.VERIFICATION_FILES, type, clearFilters, params],
    queryFn: () => getVerificationFiles(type, clearFilters, params),
    staleTime: 1000 * 60 * 5,
    enabled,
  });
};
